/// <amd-module name="Repos/VersionDropdown/GitRefFavoritesService" />
import { IReadyableReadonlyObservableArray, ObservableArray, ReadyableObservableArray } from "VSS/Core/Observable";

import { Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesCollection } from "Favorites/Common/FavoritesService";
import { FavoritePickerDataService } from "Favorites/Dropdown/ArtifactDropdownProvider";

import { GitRefFavorite, GitVersionOptions, GitVersionType, RefFavoriteType } from "Repos/Common/Contracts/Git";
import { branchNameToRef, getFriendlyName, refToBranchName } from "Repos/Common/Util/Ref";
import { getVersionString } from "Repos/Common/Util/Version";
import { IGitRestClient } from "Repos/VersionDropdown/RestClient/Git";

export namespace GitRefFavoriteArtifactConstants {
    export const artifactType = "Microsoft.TeamFoundation.Git.GitRefFavorite";
    export const artifactScopeType = "Repository";
}

class FavoritesCollection extends ObservableArray<Favorite> implements IFavoritesCollection {
    public dispose(): void {
        // Nothing to dispose. Declared to fulfill interface.
    }
}

/**
 * Façade in front of GitRestClient to make it behave as a Favorites service for a picker.
 */
export class GitRefFavoritesService implements FavoritePickerDataService {
    private _favoritesCollection: FavoritesCollection = new FavoritesCollection([]);
    private _favoritesPromise: Promise<GitRefFavorite[]>;

    constructor(private gitRestClient: IGitRestClient, private canFavorites: boolean, private projectId: string) {}

    public canUseFavorites(): boolean {
        return this.canFavorites;
    }

    public canFetchExtendedDetails(): boolean {
        return false;
    }

    public async getFavorites(artifactType: string, artifactScopeType?: string, artifactScopeId?: string): Promise<IFavoritesCollection> {
        if (artifactType !== GitRefFavoriteArtifactConstants.artifactType) {
            throw new Error(`Type ${artifactType} not valid. GitRefFavoritesService only supports ${GitRefFavoriteArtifactConstants.artifactType}.`);
        }

        if (artifactScopeType !== GitRefFavoriteArtifactConstants.artifactScopeType) {
            throw new Error(
                `Scope ${artifactScopeType} not valid. GitRefFavoritesService only supports ${GitRefFavoriteArtifactConstants.artifactScopeType}.`
            );
        }

        if (!this._favoritesPromise) {
            this._favoritesPromise = this.gitRestClient.getRefFavorites(this.projectId, artifactScopeId);
        }

        return this._favoritesPromise.then(favorites => {
            this._favoritesCollection = new FavoritesCollection(
                favorites.filter(gitRefFavorite => gitRefFavorite.type === RefFavoriteType.Ref).map(convertToArtifactFavorite)
            );

            return this._favoritesCollection;
        });
    }

    public getCurrentFolderFavorites(): IReadyableReadonlyObservableArray<string> {
        if (!this._favoritesPromise) {
            throw new Error(`Favorites not loading yet.`);
        }
        const favoritesArray = new ReadyableObservableArray<string>();
        this._favoritesPromise.then(favorites => {
            favoritesArray.push(
                ...favorites.filter(gitRefFavorite => gitRefFavorite.type === RefFavoriteType.Folder).map(gitRefFavorite => gitRefFavorite.name)
            );
            favoritesArray.ready.value = true;
        });
        return favoritesArray;
    }

    public async addFavorite(favorite: Favorite): Promise<Favorite> {
        this._favoritesCollection.push(favorite);

        const gitRefFavorite = await this.gitRestClient.createFavorite(convertToGitRefFavorite(favorite), this.projectId);

        favorite.id = gitRefFavorite.id.toString();
        return favorite;
    }

    public async removeFavorite(favorite: Favorite): Promise<void> {
        this._favoritesCollection.removeAll(f => f.id === favorite.id);

        await this.gitRestClient.deleteRefFavorite(this.projectId, parseInt(favorite.id));
    }
}

function convertToArtifactFavorite(gitRefFavorite: GitRefFavorite): Favorite {
    return {
        id: gitRefFavorite.id.toString(),
        artifactId: getArtifactId(gitRefFavorite.name),
        artifactName: getFriendlyName(gitRefFavorite.name),
        artifactScope: {
            id: gitRefFavorite.repositoryId,
            type: GitRefFavoriteArtifactConstants.artifactScopeType
        },
        artifactType: GitRefFavoriteArtifactConstants.artifactType
    } as Favorite;
}

function convertToGitRefFavorite(favorite: Favorite): GitRefFavorite {
    return {
        id: favorite.id && parseInt(favorite.id),
        name: branchNameToRef(favorite.artifactName),
        repositoryId: favorite.artifactScope.id,
        type: RefFavoriteType.Ref
    } as GitRefFavorite;
}

function getArtifactId(name: string): string {
    return getVersionString({
        version: refToBranchName(name),
        versionOptions: GitVersionOptions.None,
        versionType: GitVersionType.Branch
    });
}
